import { useEffect, useRef, useState } from 'preact/hooks';
import { Engine } from '../engine/Engine';
import { BUTTON_MAPPING_KEY, DEFAULT_BUTTON_MAPPING, type ButtonMapping } from '../engine/input/buttons';
import { repoStore } from '../engine/stores';
import type { Notebook } from '../model/types';
import { useApp } from './app-context';
import { DebugPanel } from './DebugPanel';
import { LOW_LATENCY_KEY } from './tools';
import { Toolbar } from './Toolbar';
import { DEFAULT_PREFS, TOOL_KEY, toolFromPrefs, type ToolPrefs } from './tools';

export function NotebookView({ id }: { id: string }) {
  const { repo, device } = useApp();
  const host = useRef<HTMLDivElement>(null);
  const engineRef = useRef<Engine | null>(null);
  const [engine, setEngine] = useState<Engine | null>(null);
  const [nb, setNb] = useState<Notebook | null | undefined>(undefined);
  const [prefs, setPrefs] = useState<ToolPrefs>(DEFAULT_PREFS);
  const [debug, setDebug] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    let engine: Engine | null = null;
    let cancelled = false;
    void (async () => {
      await repo.ensureDevice(id, device);
      const notebook = await repo.getNotebook(id);
      if (cancelled) return;
      setNb(notebook && notebook.deletedAt === undefined ? notebook : null);
      if (!notebook || !host.current) return;
      const [pages, strokes, savedPrefs, mapping, dbg, lowLatency] = await Promise.all([
        repo.listPages(id),
        repo.listNotebookStrokes(id),
        repo.getSetting<ToolPrefs>(TOOL_KEY),
        repo.getSetting<ButtonMapping>(BUTTON_MAPPING_KEY),
        repo.getSetting<boolean>('debugPanel'),
        repo.getSetting<boolean>(LOW_LATENCY_KEY),
      ]);
      if (cancelled) return;
      const p = savedPrefs ?? DEFAULT_PREFS;
      setPrefs(p);
      setDebug(Boolean(dbg));
      engine = new Engine(host.current, {
        store: repoStore(repo),
        notebookId: id,
        deviceId: device.id,
        tool: toolFromPrefs(p),
        buttons: mapping ?? DEFAULT_BUTTON_MAPPING,
        onError: setError,
        lowLatency: lowLatency !== false,
        onZoom: setZoom,
      });
      engine.setPages(pages, strokes);
      setPageCount(pages.length);
      engineRef.current = engine;
      setEngine(engine);
    })();
    return () => {
      cancelled = true;
      engine?.destroy();
      engineRef.current = null;
      setEngine(null);
    };
  }, [repo, device, id]);

  const changePrefs = (p: ToolPrefs) => {
    setPrefs(p);
    engineRef.current?.setTool(toolFromPrefs(p));
    void repo.setSetting(TOOL_KEY, p);
  };

  const toggleDebug = () => {
    setDebug(!debug);
    void repo.setSetting('debugPanel', !debug);
  };

  const addPage = async () => {
    await repo.addPage(id);
    const [pages, strokes] = await Promise.all([repo.listPages(id), repo.listNotebookStrokes(id)]);
    engineRef.current?.setPages(pages, strokes);
    engineRef.current?.scrollToPage(pages.length - 1);
    setPageCount(pages.length);
  };

  return (
    <div class="screen notebook">
      <header class="topbar">
        <a class="btn" href="#/">←</a>
        <h1>{nb?.title ?? (nb === null ? 'Not found' : '…')}</h1>
        <div class="spacer" />
        <span class="hint small">{pageCount} page{pageCount === 1 ? '' : 's'}</span>
        <button
          class="btn" disabled={!engine} title="Pinch with two fingers to zoom. Tap to fit the page width."
          onClick={() => engineRef.current?.resetZoom()}
        >
          {Math.round(zoom * 100)}%
        </button>
        <button class="btn" onClick={addPage} disabled={!nb}>+ Page</button>
        <button class={`btn ${debug ? 'primary' : ''}`} onClick={toggleDebug}>Debug</button>
      </header>
      {error && (
        <div class="banner err" role="alert">
          <strong>Not saved!</strong> {error}. Back up from Settings before closing the app.
          <button class="btn small" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      <Toolbar prefs={prefs} onChange={changePrefs} />
      <div class="ink-wrap">
        {/* The engine owns everything inside ink-host; Preact renders nothing there. */}
        <div class="ink-host" ref={host} />
        {nb === null && <p class="empty overlay">Notebook not found.</p>}
        {debug && engine && <DebugPanel stats={engine.stats} />}
      </div>
    </div>
  );
}
